/**
 * Tier Configuration Service
 *
 * Fetches and caches tier configuration from Supabase.
 * Used by the pricing modal and other components that need tier info
 * independent of the user's current entitlements.
 *
 * This service provides:
 * - All tier definitions (for pricing comparison)
 * - Pricing per tier
 * - Feature definitions per tier
 * - Credit pack information
 * - Global settings (credits per minute, etc.)
 */

import { SUPABASE_URL } from './config.js';

const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour

/**
 * @typedef {Object} TierPricing
 * @property {string} billingPeriod - 'monthly' | 'annual'
 * @property {number} price - Price in dollars
 * @property {string} currency - Currency code (e.g., 'USD')
 * @property {string|null} stripePriceId - Stripe price ID
 * @property {string|null} paypalPlanId - PayPal plan ID
 */

/**
 * @typedef {Object} TierFeature
 * @property {string} key - Feature key (e.g., 'allowedVisualizers')
 * @property {string} displayLabel - Human-readable label
 * @property {*} value - Feature value
 * @property {boolean} isHighlighted - Whether to emphasize in UI
 */

/**
 * @typedef {Object} Tier
 * @property {string} id - Tier ID (e.g., 'solo', 'ensemble')
 * @property {string} displayName - Display name (e.g., 'Solo', 'Ensemble')
 * @property {string|null} description - Short description
 * @property {string} badgeLabel - Badge text (e.g., 'SOLO', 'ENSEMBLE')
 * @property {number} sortOrder - Display order
 * @property {boolean} isDefault - Is default tier for new users
 * @property {boolean} isPurchasable - Show in pricing modal
 * @property {boolean} isPaid - Is this a paid tier
 * @property {string|null} highlightLabel - Label like 'Most Popular'
 * @property {TierPricing[]} pricing - Pricing options
 * @property {TierFeature[]} features - Feature list
 */

/**
 * @typedef {Object} CreditPack
 * @property {string} id - Pack ID
 * @property {string} name - Pack name
 * @property {number} credits - Number of credits
 * @property {number} price - Price in dollars
 * @property {string} currency - Currency code
 * @property {string|null} description - Description
 * @property {string|null} stripePriceId - Stripe price ID
 * @property {string|null} paypalProductId - PayPal product ID
 */

/**
 * @typedef {Object} TierConfig
 * @property {Tier[]} tiers - All tier definitions
 * @property {CreditPack[]} creditPacks - Credit pack options
 * @property {{creditsPerMinute: number, defaultCurrency: string}} settings - Global settings
 */

class TierConfigService {
  constructor() {
    /** @type {TierConfig|null} */
    this.cache = null;
    /** @type {number|null} */
    this.cacheExpiry = null;
    /** @type {Promise<TierConfig>|null} */
    this.pendingFetch = null;
  }

  /**
   * Get the full tier configuration.
   * Returns cached data if available and not expired.
   * @param {boolean} forceRefresh - Force a fresh fetch
   * @returns {Promise<TierConfig>}
   */
  async getTierConfig(forceRefresh = false) {
    // Return cached data if valid
    if (!forceRefresh && this.cache && this.cacheExpiry && Date.now() < this.cacheExpiry) {
      return this.cache;
    }

    // If a fetch is already in progress, wait for it
    if (this.pendingFetch) {
      return this.pendingFetch;
    }

    // Fetch fresh data
    this.pendingFetch = this.fetchTierConfig();

    try {
      const config = await this.pendingFetch;
      this.cache = config;
      this.cacheExpiry = Date.now() + CACHE_DURATION_MS;
      return config;
    } finally {
      this.pendingFetch = null;
    }
  }

  /**
   * Fetch tier configuration from the server.
   * @private
   * @returns {Promise<TierConfig>}
   */
  async fetchTierConfig() {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/get-tier-config`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch tier config: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get all tiers.
   * @returns {Promise<Tier[]>}
   */
  async getTiers() {
    const config = await this.getTierConfig();
    return config.tiers;
  }

  /**
   * Get tiers that can be purchased (shown in pricing modal).
   * @returns {Promise<Tier[]>}
   */
  async getPurchasableTiers() {
    const config = await this.getTierConfig();
    return config.tiers.filter(t => t.isPurchasable || !t.isPaid);
  }

  /**
   * Get a specific tier by ID.
   * @param {string} tierId - Tier ID
   * @returns {Promise<Tier|undefined>}
   */
  async getTier(tierId) {
    const config = await this.getTierConfig();
    return config.tiers.find(t => t.id === tierId);
  }

  /**
   * Check if a tier is a paid tier.
   * @param {string} tierId - Tier ID
   * @returns {Promise<boolean>}
   */
  async isPaidTier(tierId) {
    const tier = await this.getTier(tierId);
    return tier?.isPaid || false;
  }

  /**
   * Get the display name for a tier.
   * @param {string} tierId - Tier ID
   * @returns {Promise<string>}
   */
  async getTierDisplayName(tierId) {
    const tier = await this.getTier(tierId);
    return tier?.displayName || tierId;
  }

  /**
   * Get the badge label for a tier.
   * @param {string} tierId - Tier ID
   * @returns {Promise<string>}
   */
  async getTierBadgeLabel(tierId) {
    const tier = await this.getTier(tierId);
    return tier?.badgeLabel || tierId.toUpperCase();
  }

  /**
   * Get pricing for a tier.
   * @param {string} tierId - Tier ID
   * @param {string} billingPeriod - 'monthly' | 'annual'
   * @returns {Promise<TierPricing|undefined>}
   */
  async getTierPricing(tierId, billingPeriod = 'monthly') {
    const tier = await this.getTier(tierId);
    return tier?.pricing.find(p => p.billingPeriod === billingPeriod);
  }

  /**
   * Get all credit packs.
   * @returns {Promise<CreditPack[]>}
   */
  async getCreditPacks() {
    const config = await this.getTierConfig();
    return config.creditPacks;
  }

  /**
   * Get credits per minute setting.
   * @returns {Promise<number>}
   */
  async getCreditsPerMinute() {
    const config = await this.getTierConfig();
    return config.settings.creditsPerMinute;
  }

  /**
   * Get the default tier (for new users).
   * @returns {Promise<Tier|undefined>}
   */
  async getDefaultTier() {
    const config = await this.getTierConfig();
    return config.tiers.find(t => t.isDefault);
  }

  /**
   * Clear the cache (e.g., after subscription change).
   */
  clearCache() {
    this.cache = null;
    this.cacheExpiry = null;
  }
}

// Export singleton instance
export const tierConfigService = new TierConfigService();
